"use client";
/* eslint-disable react-hooks/set-state-in-effect */

import { useEffect, useState } from "react";
import { useRouter } from "next/navigation";
import { api } from "@/lib/api-client";
import { Skeleton } from "@/components/ui/Skeleton";
import StoreRenderer, { type StorePayload } from "./StoreRenderer";

/**
 * Initial state dari window.__TENANT_HOME__ (disuntik edge, lihat
 * resolveTenantHomeData di apps/web/functions/_lib/tenant.ts) — menghindari
 * StoreSkeleton tampil dulu sampai fetch client di loadStore() selesai.
 * loadStore() TETAP jalan sebagai revalidasi diam-diam, bukan dihapus.
 */
function storeFromHomeData(): StorePayload | null {
  if (typeof window === "undefined") return null;
  const home = window.__TENANT_HOME__;
  if (!home || home.path !== "/api/public/store") return null;
  const res = home.body as { success?: boolean; data?: StorePayload } | undefined;
  if (!res?.success || !res.data?.enabled) return null;
  return res.data;
}

function StoreSkeleton() {
  return (
    <main id="main-content" tabIndex={-1} className="min-h-screen bg-[#f1f1f5]">
      <div className="mx-auto max-w-[44rem] px-5 pb-24 pt-9">
        <Skeleton className="aspect-[3/1] w-full rounded-3xl" />
        <div className="-mt-11 flex flex-col items-center rounded-3xl border border-black/5 bg-white/70 p-6 shadow-sm backdrop-blur">
          <Skeleton className="h-[5.5rem] w-[5.5rem] rounded-full" />
          <Skeleton className="mt-4 h-6 w-44 rounded" />
          <Skeleton className="mt-2 h-4 w-60 rounded" />
        </div>
        <div className="mt-7 space-y-4">
          <Skeleton className="h-14 w-full rounded-2xl" />
          <Skeleton className="h-14 w-full rounded-2xl" />
          <div className="grid grid-cols-2 gap-4 pt-2 lg:grid-cols-3">
            {[1, 2, 3].map((i) => (
              <Skeleton key={i} className="h-48 rounded-2xl" />
            ))}
          </div>
        </div>
      </div>
    </main>
  );
}

/**
 * Halaman etalase publik. `redirectWhenDisabled` = true dipakai saat store
 * dijadikan beranda '/' (store_as_home): jika ternyata nonaktif, alihkan ke
 * /login (fallback aman, seperti RootPage). Di rute /store sendiri, tampilkan
 * empty-state agar tak mengagetkan.
 */
export default function StorePageClient({
  redirectWhenDisabled = false,
}: {
  redirectWhenDisabled?: boolean;
}) {
  const router = useRouter();
  const [store, setStore] = useState<StorePayload | null>(storeFromHomeData);
  const [done, setDone] = useState(false);
  const [loadError, setLoadError] = useState(false);

  const loadStore = () => {
    setDone(false);
    setLoadError(false);
    api<StorePayload>("/api/public/store").then((res) => {
      if (res.success && res.data?.enabled) {
        setStore(res.data);
      } else if (!res.success) {
        // Gagal jaringan/server — bedakan dari etalase yang sengaja dinonaktifkan.
        setLoadError(true);
      } else if (redirectWhenDisabled) {
        router.replace("/login");
      }
      setDone(true);
    });
  };

  useEffect(() => {
    loadStore();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  if (!done && !store) return <StoreSkeleton />;

  if (!store) {
    return (
      <main id="main-content" tabIndex={-1} className="flex min-h-screen items-center justify-center bg-[var(--surface,#F7F7F8)] px-5">
        <div className="text-center">
          {loadError ? (
            <>
              <p className="text-sm font-semibold text-warm-black">Gagal memuat etalase</p>
              <p className="mt-1 text-xs text-gray-500">Periksa koneksi internet kamu.</p>
              <button onClick={loadStore} className="mt-3 text-xs font-semibold text-[var(--brand-primary)] hover:underline">Coba Lagi</button>
            </>
          ) : (
            <>
              <p className="text-sm font-semibold text-warm-black">Etalase belum tersedia</p>
              <p className="mt-1 text-xs text-gray-500">Toko ini belum diaktifkan oleh pemiliknya.</p>
            </>
          )}
        </div>
      </main>
    );
  }

  return <StoreRenderer store={store} />;
}
